import React, { useState } from 'react';
import { Badge } from '../../../../components/badge';
import type { SurveySummaryQuestion } from '../../types/survey.types';
import ViewAllResponsesModal from './ViewAllResponsesModal';

interface PhoneResponseProps {
    question: SurveySummaryQuestion;
    totalResponses: number;
}

const PhoneResponse: React.FC<PhoneResponseProps> = ({ 
    question, 
    totalResponses 
}) => {
    const { position, title, type, answered_count, options } = question;
    const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false);
    
    // Calculate response rate
    const responseRate = totalResponses > 0 ? ((answered_count / totalResponses) * 100).toFixed(1) : '0.0';
    
    // Sort options by count (descending) for better visual hierarchy
    const sortedOptions = [...options].sort((a, b) => b.count - a.count);
    
    // Limit displayed responses to 5 as shown in the image
    const displayedOptions = sortedOptions.slice(0, 5);
    
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 p-6 shadow-sm">
            {/* Header Section */}
            <div className="flex items-start gap-3 mb-6">
                {/* Question Icon - Phone */}
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 bg-[#F2FCFA] dark:bg-emerald-900/30">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.1266 13.826C12.2987 13.9051 12.4926 13.9231 12.6763 13.8772C12.8601 13.8313 13.0227 13.7242 13.1374 13.5735L13.4333 13.186C13.5885 12.979 13.7898 12.811 14.0212 12.6953C14.2527 12.5796 14.5079 12.5194 14.7666 12.5194H17.2666C17.7086 12.5194 18.1326 12.695 18.4451 13.0075C18.7577 13.3201 18.9333 13.744 18.9333 14.186V16.686C18.9333 17.1281 18.7577 17.552 18.4451 17.8645C18.1326 18.1771 17.7086 18.3527 17.2666 18.3527C13.2884 18.3527 9.47305 16.7723 6.66 13.9593C3.84695 11.1463 2.2666 7.33095 2.2666 3.3527C2.2666 2.91067 2.4422 2.48675 2.75476 2.17419C3.06732 1.86163 3.49124 1.68604 3.93327 1.68604H6.43327C6.8753 1.68604 7.29922 1.86163 7.61178 2.17419C7.92434 2.48675 8.09993 2.91067 8.09993 3.3527V5.8527C8.09993 6.11144 8.03969 6.36663 7.92398 6.59806C7.80827 6.82948 7.64026 7.03079 7.43327 7.18604L7.04327 7.47854C6.89028 7.59535 6.78245 7.76152 6.73809 7.94883C6.69374 8.13613 6.71559 8.33302 6.79993 8.50604C7.93884 10.8193 9.81195 12.69 12.1266 13.826Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
                
                <div className="flex-1">
                    {/* Question Title */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {position}. {title}
                    </h3>
                    
                    {/* Question Type Badge and Response Info */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <Badge 
                            variant="outline" 
                            className="text-[10px] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-500 bg-white dark:bg-white"
                        >
                            {type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </Badge>
                        
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">{answered_count}</span> of <span className="font-medium">{totalResponses}</span> responses
                        </div>
                        
                        <div className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]">
                            {responseRate}% response rate
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Response List */}
            <div className="space-y-3">
                {displayedOptions.map((option, index) => (
                    <div 
                        key={option.option_id} 
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                        {/* Phone Icon */}
                        <div className="flex-shrink-0">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9.239 10.5C9.361 10.576 9.508 10.59 9.64 10.54C9.772 10.49 9.878 10.38 9.93 10.24L10.1 9.8C10.22 9.65 10.38 9.54 10.56 9.48C10.74 9.42 10.94 9.42 11.12 9.48H13.12C13.44 9.48 13.75 9.6 13.99 9.82C14.23 10.04 14.38 10.35 14.38 10.68V12.68C14.38 13.01 14.23 13.32 13.99 13.54C13.75 13.76 13.44 13.88 13.12 13.88C10.38 13.88 7.78 12.68 5.64 10.54C3.5 8.4 2.3 5.8 2.3 3.08C2.3 2.76 2.45 2.45 2.69 2.23C2.93 2.01 3.24 1.89 3.56 1.89H5.56C5.88 1.89 6.19 2.01 6.43 2.23C6.67 2.45 6.82 2.76 6.82 3.08V5.08C6.82 5.26 6.78 5.44 6.7 5.6C6.62 5.76 6.5 5.9 6.36 6L6.08 6.2C5.98 6.28 5.9 6.38 5.86 6.5C5.82 6.62 5.82 6.75 5.86 6.87C6.64 8.4 7.68 9.44 9.24 10.5Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        
                        {/* Phone Number */}
                        <p className="text-sm text-gray-900 dark:text-gray-100 flex-1">
                            {option.label}
                        </p>
                    </div>
                ))}
            </div>
            
            {/* No responses message */}
            {options.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No responses available for this question
                </div>
            ) : (
                /* Pagination Info */
                <div className="text-center mt-4 text-xs text-[#717182] dark:text-gray-400">
                    Showing {displayedOptions.length} of {options.length} responses - 
                    <button 
                        className="underline hover:no-underline cursor-pointer ml-1"
                        onClick={() => setIsViewAllModalOpen(true)}
                    >
                        View All
                    </button>
                </div>
            )}
            
            {/* View All Modal */}
            <ViewAllResponsesModal
                isOpen={isViewAllModalOpen}
                onClose={() => setIsViewAllModalOpen(false)}
                question={question}
                totalResponses={totalResponses}
            />
        </div>
    );
};

export default PhoneResponse;
